import { useRef, useState } from 'react';
import PageShell from '@components/pages/PageShell';
import { ResourceHero, DownloadGrid, Pagination, SectionLabel, EmptyList } from '@components/pages/ResourceSections';
import Report from '@components/landing/Report';
import Newsletter from '@components/landing/Newsletter';
import { DOCUMENTS } from '@/data/resources';
import { usePagedList } from '@/lib/usePagedList';
import Seo from '@components/Seo';

/** Three rows of the four-column grid. */
const PER_PAGE = 12;

export default function Publications() {
    const [query, setQuery] = useState('');
    const grid = useRef<HTMLElement>(null);
    const { page, setPage, pages, matches, pageItems } = usePagedList(
        DOCUMENTS.publications,
        PER_PAGE,
        query,
        (d) => d.text,
    );
    const searching = query.trim().length > 0;

    const goTo = (next: number) => {
        setPage(next);
        grid.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

    return (
        <PageShell>
            <Seo
                title="Our Publications"
                description="Research, policy briefs and case studies from our work across the justice sector — free to read, free to download."
            />
            <ResourceHero
                titleLead="Our"
                titleBold="Publications"
                intro="Research, policy briefs and case studies from our work across the justice sector — free to read, free to download."
                query={query}
                onSearch={setQuery}
                searchPlaceholder="Search publications"
            />

            {/* Top Featured leads the page — the full showcase with its own right rail */}
            {!searching && (
                <div className="pt-10 lg:pt-14">
                    <Report headingLead="Top" headingBold="Featured" showViewAll={false} />
                </div>
            )}

            <section
                ref={grid}
                className={`scroll-mt-24 bg-white pb-14 lg:pb-20 ${searching ? 'pt-14 lg:pt-20' : ''}`}
                aria-live="polite"
            >
                <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                        <SectionLabel>{searching ? 'Search results' : 'All Publications'}</SectionLabel>
                        <p className="text-[13px] text-[#5e5e5e]">
                            {searching
                                ? `${matches.length} for “${query.trim()}”`
                                : `${DOCUMENTS.publications.length} in total`}
                        </p>
                    </div>
                    {pageItems.length > 0 ? (
                        <DownloadGrid items={pageItems} basePath="/resources/publications" />
                    ) : (
                        <EmptyList query={query} whenSearching={`Nothing matches “${query.trim()}”.`} />
                    )}
                    <Pagination page={page} pages={pages} onChange={goTo} />
                </div>
            </section>

            <Newsletter />
        </PageShell>
    );
}
